import { useEffect, useState, useRef } from "react";
import Lenis from "lenis";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { api } from "@/lib/api";
import { Preloader } from "@/components/site/Preloader";
import { Cursor } from "@/components/site/Cursor";
import { SnakePath } from "@/components/site/SnakePath";
import { Hero } from "@/components/site/Hero";
import { StatsWall } from "@/components/site/StatsWall";
import { Timeline } from "@/components/site/Timeline";
import { Collaborations } from "@/components/site/Collaborations";
import { Family } from "@/components/site/Family";
import { MusicSection } from "@/components/site/MusicSection";
import { ServicesSection } from "@/components/site/ServicesSection";
import { PressSection } from "@/components/site/PressSection";
import { ContactSection } from "@/components/site/ContactSection";
import { Footer } from "@/components/site/Footer";

gsap.registerPlugin(ScrollTrigger);

export default function Home() {
  const [content, setContent] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const lenisRef = useRef(null);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data)).catch(() => setContent({}));
  }, []);

  useEffect(() => {
    if (!content || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    lenisRef.current = lenis;
    lenis.on("scroll", ScrollTrigger.update);
    const tick = (time) => lenis.raf(time * 1000);
    gsap.ticker.add(tick);
    gsap.ticker.lagSmoothing(0);
    return () => {
      gsap.ticker.remove(tick);
      lenis.destroy();
    };
  }, [content]);

  useEffect(() => {
    if (loaded) setTimeout(() => ScrollTrigger.refresh(), 100);
  }, [loaded]);

  useEffect(() => {
    const settings = content?.settings;
    if (settings?.seo_title) document.title = settings.seo_title;
  }, [content]);

  if (!content) return <div className="fixed inset-0 bg-[#0A0A0A]" />;

  const { texts = {}, stats = {}, music = {}, settings = {}, brands = [], services = [], timeline = [], press = [] } = content;

  return (
    <div className="site-root relative bg-[#0A0A0A]" data-testid="home-page">
      <Cursor />
      <div className="grain-overlay" aria-hidden="true" />
      <div className="vignette-overlay" aria-hidden="true" />
      {!loaded && <Preloader onDone={() => setLoaded(true)} />}
      <SnakePath />
      <main>
        <Hero texts={texts} />
        <StatsWall stats={stats} texts={texts} />
        <Timeline timeline={timeline} texts={texts} />
        <Collaborations brands={brands} texts={texts} />
        <Family texts={texts} />
        <MusicSection music={music} texts={texts} />
        <ServicesSection services={services} texts={texts} />
        <PressSection press={press} texts={texts} />
        <ContactSection texts={texts} settings={settings} />
      </main>
      <Footer settings={settings} />
    </div>
  );
}
